import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { formatDate } from '../../utils/helpers';
import toast from 'react-hot-toast';

export default function StatusUpdate({ caseData, onUpdate }) {
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [statusData, setStatusData] = useState({
    status: caseData?.status || '',
    complete_date: caseData?.complete_date || '',
    hold_reason: caseData?.hold_reason || '',
    remarks: caseData?.remarks || '',
  });

  useEffect(() => {
    if (caseData?.id) {
      loadHistory();
      setStatusData({
        status: caseData?.status || '',
        complete_date: caseData?.complete_date ? caseData.complete_date.split('T')[0] : '',
        hold_reason: caseData?.hold_reason || '',
        remarks: caseData?.remarks || '',
      });
    }
  }, [caseData]);

  const loadHistory = async () => {
    try {
      const res = await api.cases.getHistory(caseData.id);
      setHistory(Array.isArray(res) ? res : (res.history || res.data || []));
    } catch (err) {
      console.error('Failed to load history:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setStatusData((prev) => ({ ...prev, [name]: value }));
  };

  const handleUpdateStatus = async () => {
    try {
      setLoading(true);
      await api.cases.updateStatus(caseData.id, statusData);
      toast.success('Status updated successfully');
      onUpdate();
    } catch (error) {
      toast.error('Failed to update status');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const statuses = isAdmin
    ? [
        'new',
        'documents_uploaded',
        'in_process',
        'verification_started',
        'pending_clarification',
        'completed',
        'closed',
      ]
    : [
        'in_process',
        'need_action',
        'completed',
      ];

  return (
    <div className="status-update-section">
      <div className="transcript-section">
        <h3 className="transcript-section-title">Update Status</h3>
        <div className="form-grid">
          <div className="form-group">
            <label htmlFor="status" className="form-label">
              Final Status
            </label>
            <select
              id="status"
              name="status"
              value={statusData.status}
              onChange={handleInputChange}
              className="form-input"
            >
              {statuses.map((s) => (
                <option key={s} value={s}>
                  {(s || '').replace(/_/g, ' ').toUpperCase()}
                </option>
              ))}
            </select>
          </div>

          {isAdmin && (
            <div className="form-group">
              <label htmlFor="complete_date" className="form-label">
                Complete Date
              </label>
              <input
                id="complete_date"
                type="date"
                name="complete_date"
                value={statusData.complete_date}
                onChange={handleInputChange}
                className="form-input"
              />
            </div>
          )}

          {isAdmin && statusData.status === 'pending_clarification' && (
            <div className="form-group">
              <label htmlFor="hold_reason" className="form-label">
                Insufficiency/Hold Reason
              </label>
              <textarea
                id="hold_reason"
                name="hold_reason"
                value={statusData.hold_reason}
                onChange={handleInputChange}
                className="form-input"
                rows={4}
              />
            </div>
          )}

          <div className="form-group">
            <label htmlFor="remarks" className="form-label">
              Remarks
            </label>
            <textarea
              id="remarks"
              name="remarks"
              value={statusData.remarks}
              onChange={handleInputChange}
              className="form-input"
              rows={3}
            />
          </div>

          <div className="form-actions">
            <button
              className="btn btn-primary"
              onClick={handleUpdateStatus}
              disabled={loading}
            >
              {loading ? 'Updating...' : 'Update Status'}
            </button>
          </div>
        </div>
      </div>

      {history.length > 0 && (
        <div className="transcript-section">
          <h3 className="transcript-section-title">Status History</h3>
          <div className="timeline">
            {history.map((item, idx) => (
              <div key={idx} className="timeline-item">
                <div className="timeline-content">
                  <p className="timeline-date">
                    {formatDate(item.created_at)}
                  </p>
                  <p className="text-bold">
                    Status: {item?.status?.replace(/_/g, ' ').toUpperCase()}
                  </p>
                  <p className="text-sm text-gray">
                    Updated by: {item.updated_by}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
